<!-- Notifications toggle — compact brandbot-style trigger -->
<button
  class="relative flex items-center justify-center w-7 h-7 rounded-md cursor-pointer transition-all duration-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"
  (click)="op.toggle($event)"
>
  <primecontrol-svg-icon
    class="icon-size-4 text-[color:var(--text-subtitle)]"
    [svgIcon]="'heroicons_outline:bell'"
  ></primecontrol-svg-icon>
  @if (unreadCount() > 0) {
    <!-- A count, not an alarm: the badge wears the accent CTA skin (colour and
         shadow), not neon red. -->
    <span
      class="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[14px] h-[14px] px-0.5 rounded-full bg-[color:var(--main-button-bg)] text-[color:var(--main-button-text)] text-pc-eyebrow font-bold leading-none tabular-nums shadow-[0_1px_2px_rgba(15,23,42,0.18)]"
    >
      {{ unreadCount() > 9 ? '9+' : unreadCount() }}
    </span>
  }
</button>

<!-- Notifications panel — PrimeNG popover (brandbot mechanism). The width clamp
     in styleClass keeps it inside the viewport on small screens. -->
<p-popover
  #op
  appendTo="body"
  styleClass="pc-popup-surface w-[min(24rem,calc(100vw_-_1.5rem))]! max-w-[calc(100vw_-_1.5rem)]! overflow-hidden! p-0!"
  (onShow)="onPopoverShow()"
  (onHide)="onPopoverHide()"
>
  <span aria-hidden="true" class="pc-menu-grain pointer-events-none absolute inset-0"></span>

  <!-- Header — one line. The bell chip is gone: the user just clicked a bell. -->
  <div
    class="relative flex shrink-0 items-center justify-between gap-2 px-3.5 py-2.5 border-b border-neutral-200/60 dark:border-white/10"
  >
    <!-- Both texts are nowrap and the title is the only thing allowed to
         truncate: the count used to be the flex item that gave way, so a long
         action label squeezed "3 sin leer" into three stacked lines. -->
    <div class="flex items-baseline gap-2 min-w-0">
      <h3
        class="font-semibold text-pc-title-sm leading-tight text-[color:var(--text-title)] truncate"
      >
        {{ 'NOTIFICATIONS' | translate }}
      </h3>
      @if (unreadCount() > 0) {
        <span
          class="text-pc-label leading-tight text-[color:var(--text-subtitle)] tabular-nums whitespace-nowrap shrink-0"
        >
          {{ 'NOTIFICATIONS_UNREAD' | translate: { count: unreadCount() } }}
        </span>
      }
    </div>
    <div class="flex items-center gap-1 shrink-0">
      @if (unreadCount() > 0) {
        <!-- Icon, not the label: "Marcar todas como leídas" is ~130px of a
             384px panel, which is what starved the header. The translated
             string lives on as the tooltip. -->
        <button
          (click)="markAllAsRead()"
          class="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all duration-150 cursor-pointer"
          style="color: var(--main-button-bg)"
          [primecontrolTooltip]="'MARK_ALL_AS_READ' | translate"
        >
          <primecontrol-svg-icon
            class="icon-size-4"
            [svgIcon]="'mail-open'"
          ></primecontrol-svg-icon>
        </button>
      }
      <button
        (click)="op.hide()"
        class="flex items-center justify-center w-7 h-7 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all duration-150 cursor-pointer"
        [primecontrolTooltip]="'CLOSE' | translate"
      >
        <primecontrol-svg-icon
          class="icon-size-4"
          [svgIcon]="'heroicons_solid:x-mark'"
        ></primecontrol-svg-icon>
      </button>
    </div>
  </div>

  @if (actionFailed()) {
    <!-- The change the user just saw has been undone; say so in plain words. -->
    <p
      role="alert"
      class="relative px-3.5 py-2 border-b border-neutral-200/60 dark:border-white/10 text-pc-label leading-snug text-red-600 dark:text-red-400"
    >
      {{ 'NOTIFICATIONS_ACTION_FAILED' | translate }}
    </p>
  }

  <!-- Content — grouped by day, newest first -->
  <div class="relative max-h-[min(80vh,26rem)] overflow-y-auto">
    @for (group of groups(); track group.bucket) {
      <div
        class="px-3.5 pt-3 pb-1.5 text-pc-eyebrow font-bold uppercase tracking-widest leading-tight text-slate-500 dark:text-neutral-500"
      >
        {{ group.labelKey | translate }}
      </div>

      @for (row of group.rows; track row.source.id) {
        <!-- The whole row is the target: an anchor when the notification links
             somewhere, a plain div otherwise. The unread accent sits on the
             leading edge — it used to be a dot that looked decorative but was
             the read toggle. -->
        <!-- Type scale, in real pixels (these are fixed-px classes, so the 90%
             root does not shrink them): 14 panel title / 13 row hero / 11 every
             supporting line (type, detail, time, unread count) / 10 only for the
             eyebrow recipe, whose uppercase reads a size larger than it is. Four
             steps instead of the previous 14-12-12-11-10-10 mix. -->
        <a
          class="group/row relative flex items-start gap-2.5 pl-3 pr-3.5 py-2 border-t border-neutral-200/50 dark:border-white/[0.06] transition-colors duration-150 hover:bg-neutral-50 dark:hover:bg-neutral-800/40"
          [class.cursor-pointer]="row.source.link"
          [routerLink]="row.route"
          [attr.href]="!row.source.useRouter && row.source.link ? row.source.link : null"
          (click)="open(row)"
        >
          <!-- Unread marker: a hairline bar, not a control. It carries the read
               state together with the hero's ink; the row no longer fades to
               opacity-60, which dimmed real text (including the red deadline)
               instead of restating it. -->
          <span
            aria-hidden="true"
            class="absolute left-0 top-0 bottom-0 w-[3px]"
            [style.background-color]="!row.source.read ? 'var(--main-button-bg)' : 'transparent'"
          ></span>

          @if (row.source.image) {
            <img
              class="shrink-0 w-7 h-7 rounded-lg object-cover object-center mt-0.5"
              [src]="row.source.image"
              alt=""
            />
          } @else {
            <primecontrol-svg-icon
              class="icon-size-3.5 shrink-0 mt-0.5 text-[color:var(--text-subtitle)]"
              [svgIcon]="row.icon"
            ></primecontrol-svg-icon>
          }

          <div class="flex-1 min-w-0">
            <!-- Reading order is record → what happened → when: the folio leads
                 because it is the only text that differs between rows, and the
                 type follows in sentence case as a supporting line. It used to be
                 the eyebrow recipe, which is for two-word structural labels — a
                 whole sentence set in bold uppercase with wide tracking became the
                 loudest thing in the row while saying the least, and cost enough
                 extra width to truncate itself ("COTIZACIÓN VENCIDA SIN RESPUES…").
                 Nothing here truncates now: two lines, then clamp. -->
            @if (row.subject) {
              <div
                class="text-pc-body font-semibold leading-snug line-clamp-2 {{
                  row.source.read
                    ? 'text-neutral-500 dark:text-neutral-400'
                    : 'text-[color:var(--text-title)]'
                }}"
              >
                {{ row.subject }}
              </div>
              @if (row.source.title) {
                <div
                  class="text-pc-label leading-snug text-[color:var(--text-subtitle)] line-clamp-2"
                >
                  {{ row.source.title | translate }}
                </div>
              }
            } @else if (row.source.title) {
              <!-- No description: the type is all there is, so it takes the hero
                   line instead of leaving a supporting line over an empty row. -->
              <div
                class="text-pc-body font-semibold leading-snug line-clamp-2 {{
                  row.source.read
                    ? 'text-neutral-500 dark:text-neutral-400'
                    : 'text-[color:var(--text-title)]'
                }}"
              >
                {{ row.source.title | translate }}
              </div>
            }
            @if (row.detail; as detail) {
              <!-- The actionable fact. Same size as the type above it but a step
                   heavier, so emphasis comes from weight and ink, never from a
                   coloured box. -->
              <div
                class="text-pc-label font-medium leading-snug line-clamp-2 {{
                  detail.danger
                    ? 'text-red-600 dark:text-red-400'
                    : detail.warning
                      ? 'text-amber-700 dark:text-amber-400'
                      : 'text-[color:var(--text-subtitle)]'
                }}"
              >
                <!-- A name or a date the backend wrote is shown as written; only
                     catalog keys go through the translator. -->
                {{ detail.text ?? (detail.labelKey | translate: detail.params) }}
              </div>
            }
          </div>

          <!-- Trailing: the relative time, with the actions fading in over it.
               The time keeps its space (opacity, not display) and the actions are
               absolute, so revealing them can't reflow the row. That overlay is
               for a mouse only: a touch screen has no hover, so there the actions
               sit after the time instead of on top of it. -->
          <div class="relative shrink-0 flex items-center self-center h-6">
            @if (row.time; as time) {
              <span
                class="text-pc-label tabular-nums text-[color:var(--text-subtitle)] transition-opacity duration-150 pointer-fine:group-hover/row:opacity-0"
                [primecontrolTooltip]="time.exact"
              >
                {{ time.labelKey | translate: time.params }}
              </span>
            }
            <div
              class="ml-2 flex items-center gap-2 pointer-fine:ml-0 pointer-fine:absolute pointer-fine:right-0 pointer-fine:top-0 pointer-fine:bottom-0 pointer-fine:gap-0.5 transition-opacity duration-150 pointer-fine:opacity-0 pointer-fine:pointer-events-none pointer-fine:group-hover/row:opacity-100 pointer-fine:group-hover/row:pointer-events-auto"
            >
              <button
                class="pc-touch-target flex items-center justify-center w-6 h-6 rounded-md text-neutral-400 hover:text-[color:var(--text-title)] hover:bg-neutral-100 dark:hover:bg-neutral-700/60 transition-colors cursor-pointer"
                (click)="toggleRead(row.source, $event)"
                [primecontrolTooltip]="
                  (row.source.read ? 'MARK_AS_UNREAD' : 'MARK_AS_READ') | translate
                "
              >
                <primecontrol-svg-icon
                  class="icon-size-3.5"
                  [svgIcon]="
                    row.source.read ? 'heroicons_outline:eye-off' : 'heroicons_outline:check'
                  "
                ></primecontrol-svg-icon>
              </button>
              <button
                class="pc-touch-target flex items-center justify-center w-6 h-6 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors cursor-pointer"
                (click)="delete(row.source, $event)"
                [primecontrolTooltip]="'REMOVE' | translate"
              >
                <primecontrol-svg-icon
                  class="icon-size-3.5"
                  [svgIcon]="'heroicons_outline:trash'"
                ></primecontrol-svg-icon>
              </button>
            </div>
          </div>
        </a>
      }
    } @empty {
      <div class="flex flex-col items-center justify-center text-center px-6 py-12">
        <div
          class="flex items-center justify-center w-14 h-14 rounded-2xl bg-neutral-100 dark:bg-neutral-800 mb-4"
        >
          <primecontrol-svg-icon
            class="icon-size-6 text-neutral-400 dark:text-neutral-500"
            [svgIcon]="'heroicons_outline:bell'"
          ></primecontrol-svg-icon>
        </div>
        <p class="text-pc-body font-semibold text-[color:var(--text-title)] mb-1">
          {{ 'NO_NOTIFICATIONS' | translate }}
        </p>
        <p class="text-pc-secondary leading-relaxed text-[color:var(--text-subtitle)] max-w-52">
          {{ 'NO_NOTIFICATIONS_HINT' | translate }}
        </p>
      </div>
    }
  </div>
</p-popover>
